import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isPurchaseUser } from "../utils/permissions";
import { authFetch } from "../utils/authFetch";

function ComparisonListPage() {
  const navigate = useNavigate();

  // State Management
  const [rfqs, setRfqs] = useState([]);
  const [selectedSite, setSelectedSite] = useState("All Sites");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 💎 Sleek New Alert State Management Layers
  const [modal, setModal] = useState({
    isOpen: false,
    type: "confirm", // 'confirm' or 'success' or 'error'
    title: "",
    message: "",
    targetId: null,
  });

 useEffect(() => {
  authFetch(`${process.env.REACT_APP_API_URL}/comparison-list`)
    .then((res) => res.json())
    .then((data) => setRfqs(data))
    .catch((err) => console.error("Error fetching RFQs:", err));
}, []);

  // UI Dynamic Status Colors
  const statusColor = (status) => {
    switch (status) {
      case "Approved":
        return { text: "#16a34a", bg: "#f0fdf4" };
      case "Rejected":
        return { text: "#dc2626", bg: "#fef2f2" };
      default:
        return { text: "#d97706", bg: "#fffbeb" };
    }
  };

  // Trigger Confirmation Intercept
  const askDeleteConfirmation = (id) => {
    setModal({
      isOpen: true,
      type: "confirm",
      title: "Confirm Deletion",
      message: "Are you sure you want to permanently delete this RFQ comparison? This action cannot be undone.",
      targetId: id
    });
  };

  // Execution Handler after confirmation click
  const executeDelete = async () => {
    const id = modal.targetId;
    
    // Close confirmation dialog while processing
    setModal((prev) => ({ ...prev, isOpen: false }));

    try {
     const res = await authFetch(
  `${process.env.REACT_APP_API_URL}/comparison/${id}`,
  {
    method: "DELETE",
  }
);

      if (res.ok) {
        setRfqs(rfqs.filter((rfq) => rfq.id !== id));
        
        // Show Sleek Professional Success Confirmation State
        setTimeout(() => {
          setModal({
            isOpen: true,
            type: "success",
            title: "Record Deleted",
            message: "The RFQ comparison matrix entry has been removed successfully from the master database.",
            targetId: null
          });
        }, 100);

      } else {
        triggerErrorNotification();
      }
    } catch (err) {
      console.error(err);
      triggerErrorNotification();
    }
  };

  const triggerErrorNotification = () => {
    setModal({
      isOpen: true,
      type: "error",
      title: "Execution Failure",
      message: "Unable to drop database entry rows. Please check network link connections.",
      targetId: null
    });
  };

  // Derive unique sites list for the interactive selector sidebar
  const sitesList = ["All Sites", ...new Set(rfqs.map((r) => r.site_name).filter(Boolean))];

  // Filter Logic
  const filteredRfqs =
    selectedSite === "All Sites"
      ? rfqs
      : rfqs.filter((rfq) => rfq.site_name === selectedSite);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRfqs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRfqs.length / itemsPerPage);

  const handleSiteChange = (site) => {
    setSelectedSite(site);
    setCurrentPage(1); // Reset page on filter switch
  };

  return (
    <div style={containerLayout}>
      
      {/* 🌟 SLICK INTEGRATED DIALOG WORKSPACE (Replaces old browser alerts) */}
      {modal.isOpen && (
        <div style={modalBackdropOverlay}>
          <div style={modalSurfaceCard}>
            
            {/* Context Header Icon Indicator */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
              {modal.type === "confirm" && (
                <div style={{ ...iconCircleBase, bg: "#fef2f2", color: "#dc2626" }}>⚠️</div>
              )}
              {modal.type === "success" && (
                <div style={{ ...iconCircleBase, bg: "#f0fdf4", color: "#16a34a" }}>✅</div>
              )}
              {modal.type === "error" && (
                <div style={{ ...iconCircleBase, bg: "#fffbeb", color: "#d97706" }}>❌</div>
              )}
            </div>

            <h3 style={modalTitleText}>{modal.title}</h3>
            <p style={modalMessageDescription}>{modal.message}</p>

            {/* Action Bar Render Blocks */}
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "24px" }}>
              {modal.type === "confirm" ? (
                <>
                  <button 
                    onClick={() => setModal({ isOpen: false, type: "confirm", title: "", message: "", targetId: null })}
                    style={modalCancelBtn}
                  >
                    Cancel
                  </button>
                  <button onClick={executeDelete} style={modalConfirmDangerBtn}>
                    Delete Record
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => setModal({ isOpen: false, type: "confirm", title: "", message: "", targetId: null })}
                  style={modalCloseInfoBtn}
                >
                  Acknowledge
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* LEFT SIDEBAR: SITE FILTER */}
      <div style={sidebarStyle}>
        <h3 style={sidebarTitle}>Filter by Site</h3>
        <div style={sidebarList}>
          {sitesList.map((site) => (
            <button
              key={site}
              onClick={() => handleSiteChange(site)}
              style={{
                ...sidebarItemBtn,
                background: selectedSite === site ? "#eff6ff" : "transparent",
                color: selectedSite === site ? "#2563eb" : "#475569",
                fontWeight: selectedSite === site ? "600" : "400",
              }}
            >
              <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                {site}
              </span>
              <span style={counterBadge}>
                {site === "All Sites" ? rfqs.length : rfqs.filter(r => r.site_name === site).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT MAIN CONTENT AREA */}
      <div style={mainContentStyle}>
        
        {/* PAGE HEADER */}
        <div style={{ marginBottom: "20px" }}>
          <h1 style={headerTitle}>Comparison Dashboard</h1>
          <p style={headerSubtitle}>
            Viewing <strong>{selectedSite}</strong> &bull; Managing vendor quotation comparison matrices
          </p>
        </div>

        {/* TABLE WRAPPER CONTAINER */}
        <div style={tableCardStyle}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc", textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>
                  <th style={thStyle}>RFQ ID</th>
                  <th style={thStyle}>Site Details</th>
                  <th style={thStyle}>Metrics (Items)</th>
                  <th style={thStyle}>Quotations Rec'd</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Created Date</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((rfq) => {
                    const colors = statusColor(rfq.status);
                    return (
                      <tr key={rfq.id} style={trRowStyle}>
                        <td style={{ ...tdStyle, fontWeight: "600", color: "#2563eb" }}>
                          #{rfq.id}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: "500", color: "#1e293b" }}>
                            {rfq.site_name || "Unassigned"}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <span style={textMuted}>{rfq.total_items} Items</span> / <span style={textMuted}>{rfq.total_vendors} Vendors</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={quoteBadgeStyle}>
                            {rfq.quotation_received} / {rfq.total_vendors} Rec'd
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            background: colors.bg,
                            color: colors.text,
                            padding: "4px 10px",
                            borderRadius: "12px",
                            fontWeight: "600",
                            fontSize: "12px",
                            display: "inline-block"
                          }}>
                            {rfq.status || "Pending"}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, color: "#64748b" }}>
                          {rfq.created_at ? new Date(rfq.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          <div style={{ display: "inline-flex", gap: "6px" }}>
                            <button
                              onClick={() => navigate(`/comparison/${rfq.id}`)}
                              style={viewBtn}
                            >
                              View
                            </button>
                            {!isPurchaseUser() && (
  <button
    onClick={() => askDeleteConfirmation(rfq.id)}
    style={deleteBtn}
  >
    Delete
  </button>
)}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" style={{ ...tdStyle, textAlign: "center", padding: "40px", color: "#94a3b8" }}>
                      No data records found for this selection.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION COMPONENT */}
          {totalPages > 1 && (
            <div style={paginationRow}>
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                Showing <strong>{indexOfFirstItem + 1}</strong> to{" "}
                <strong>{Math.min(indexOfLastItem, filteredRfqs.length)}</strong> of{" "}
                <strong>{filteredRfqs.length}</strong> items
              </div>
              <div style={{ display: "flex", gap: "4px" }}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  style={{ ...paginationBtn, opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, idx) => (
                  <button
                    key={idx + 1}
                    onClick={() => setCurrentPage(idx + 1)}
                    style={{
                      ...paginationBtn,
                      background: currentPage === idx + 1 ? "#2563eb" : "#fff",
                      color: currentPage === idx + 1 ? "#fff" : "#475569",
                      borderColor: currentPage === idx + 1 ? "#2563eb" : "#e2e8f0",
                    }}
                  >
                    {idx + 1}
                  </button>
                ))}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  style={{ ...paginationBtn, opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

/* --- ADVANCED INLINE CSS OBJECTS --- */

// 🌐 Beautiful High-End Overlay Backdrop Blur Framework
const modalBackdropOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: "rgba(15, 23, 42, 0.45)", // Tint matching dashboard colors
  backdropFilter: "blur(4px)",            // Soft blur background
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10000,
  animation: "fadeIn 0.2s ease-out"
};

const modalSurfaceCard = {
  background: "#ffffff",
  width: "100%",
  maxWidth: "400px",
  padding: "32px 24px",
  borderRadius: "16px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  textAlign: "center",
  border: "1px solid #f1f5f9"
};

const iconCircleBase = {
  width: "56px",
  height: "56px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "24px"
};

const modalTitleText = {
  fontSize: "18px",
  fontWeight: "700",
  color: "#0f172a",
  margin: "0 0 8px 0"
};

const modalMessageDescription = {
  fontSize: "14px",
  color: "#64748b",
  lineHeight: "1.5",
  margin: 0
};

const modalCancelBtn = {
  padding: "10px 18px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#334155",
  fontWeight: "600",
  fontSize: "14px",
  cursor: "pointer"
};

const modalConfirmDangerBtn = {
  padding: "10px 18px",
  borderRadius: "8px",
  border: "none",
  background: "#dc2626",
  color: "#ffffff",
  fontWeight: "600",
  fontSize: "14px",
  cursor: "pointer"
};

const modalCloseInfoBtn = {
  padding: "10px 24px",
  borderRadius: "8px",
  border: "none",
  background: "#0f172a",
  color: "#ffffff",
  fontWeight: "600",
  fontSize: "14px",
  cursor: "pointer"
};

const containerLayout = {
  display: "flex",
  gap: "24px",
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  background: "#f8fafc",
  padding: "24px",
  minHeight: "100vh",
  boxSizing: "border-box"
};

const sidebarStyle = {
  width: "260px",
  background: "#fff",
  borderRadius: "16px",
  padding: "20px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.02)",
  flexShrink: 0,
  alignSelf: "flex-start",
};

const sidebarTitle = {
  fontSize: "14px",
  fontWeight: "700",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#94a3b8",
  margin: "0 0 14px 0",
};

const sidebarList = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const sidebarItemBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "10px 14px",
  borderRadius: "10px",
  border: "none",
  textAlign: "left",
  fontSize: "14px",
  cursor: "pointer",
  transition: "all 0.15s ease",
};

const counterBadge = {
  background: "#f1f5f9",
  color: "#64748b",
  fontSize: "11px",
  fontWeight: "600",
  padding: "2px 8px",
  borderRadius: "20px",
};

const mainContentStyle = {
  flexGrow: 1,
  minWidth: 0, 
};

const headerTitle = {
  margin: 0,
  fontSize: "28px",
  fontWeight: "700",
  color: "#0f172a",
};

const headerSubtitle = {
  color: "#64748b",
  marginTop: "4px",
  fontSize: "14px",
};

const tableCardStyle = {
  background: "#fff",
  borderRadius: "16px",
  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.02)",
  border: "1px solid #e2e8f0",
  overflow: "hidden"
};

const thStyle = {
  padding: "12px 16px",
  fontSize: "13px",
  fontWeight: "600",
  color: "#64748b",
};

const tdStyle = {
  padding: "11px 16px", 
  fontSize: "13.5px",
  color: "#334155",
  verticalAlign: "middle"
};

const trRowStyle = {
  borderBottom: "1px solid #f1f5f9",
  transition: "background 0.1s ease",
};

const textMuted = {
  color: "#64748b"
};

const quoteBadgeStyle = {
  background: "#eff6ff",
  color: "#2563eb",
  padding: "4px 10px",
  borderRadius: "12px",
  fontWeight: "600",
  fontSize: "12px",
};

const viewBtn = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  padding: "6px 12px",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "600",
  fontSize: "12.5px"
};

const deleteBtn = {
  background: "#fff",
  color: "#dc2626",
  border: "1px solid #fca5a5",
  padding: "6px 12px",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "500",
  fontSize: "12.5px"
};

const paginationRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "14px 16px",
  borderTop: "1px solid #e2e8f0",
  background: "#ffffff"
};

const paginationBtn = {
  padding: "5px 10px",
  fontSize: "12px",
  fontWeight: "500",
  borderRadius: "6px",
  border: "1px solid #e2e8f0",
  background: "#fff",
  color: "#475569",
  cursor: "pointer"
};

export default ComparisonListPage;